<script setup>
import { ref, computed, watch, onMounted, onActivated } from 'vue';
import { useRouter } from 'vue-router';
import { Table, Segmented, Button, Spin, Tag } from 'ant-design-vue';
import {
    CalendarOutlined,
    UnorderedListOutlined,
    VideoCameraOutlined,
    ShopOutlined
} from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import { taipeiDay } from '@/utils/taipeiTime';
import { getConsultantBookings, getConsultantDashboard } from '@/api/consultation-workspace/consultationWorkspaceApi';
import BookingCalendar from '@/components/consultation-workspace/BookingCalendar.vue';
import ConsultantLevelBadge from '@/components/consultation-workspace/ConsultantLevelBadge.vue';
import {
    formatConsultationTimeRange,
    getConsultationBookingStatusMeta
} from '@/utils/consultationBookingStatus';

defineOptions({ name: 'ConsultationWorkspaceBookings' });

const router = useRouter();
const loading = ref(false);
const displayMode = ref('calendar');
const calendarView = ref('week');
const anchorDate = ref(dayjs());
const filter = ref('upcoming');
const bookings = ref([]);
const pagination = ref({ page: 1, page_size: 20, total: 0 });
const consultant = ref(null);
const todayBookings = ref([]);
const lastLoadedAt = ref(0);
const STALE_MS = 60_000;
let calendarTimer = null;

const todayCount = computed(() => todayBookings.value.length);

const columns = [
    { title: '時段', key: 'starts_at', width: 200 },
    { title: '學員', dataIndex: 'user_name', key: 'user_name' },
    { title: '型式', key: 'mode', width: 90 },
    { title: '諮詢狀態', key: 'status', width: 110 },
    { title: '版本', key: 'version_name' },
    { title: '操作', key: 'action', width: 160 }
];

const calendarRange = computed(() => {
    const anchor = dayjs(anchorDate.value);
    if (calendarView.value === 'day') {
        const date = anchor.format('YYYY-MM-DD');
        return { from: date, to: date };
    }
    if (calendarView.value === 'week') {
        return {
            from: anchor.startOf('week').format('YYYY-MM-DD'),
            to: anchor.endOf('week').format('YYYY-MM-DD')
        };
    }
    return {
        from: anchor.startOf('month').format('YYYY-MM-DD'),
        to: anchor.endOf('month').format('YYYY-MM-DD')
    };
});

const rangeBookingCount = computed(() => bookings.value.length);

const confirmedCount = computed(
    () => bookings.value.filter((b) => b.status === 'confirmed').length
);

const loadCalendar = async () => {
    loading.value = true;
    try {
        const { from, to } = calendarRange.value;
        const res = await getConsultantBookings({
            filter: 'all',
            from,
            to,
            page: 1,
            page_size: 100
        });
        if (res.status === 1) {
            bookings.value = (res.data.items || []).map((row) => ({
                ...row,
                key: row.id
            }));
        }
    } finally {
        loading.value = false;
    }
};

const loadTable = async () => {
    loading.value = true;
    try {
        const res = await getConsultantBookings({
            filter: filter.value,
            page: pagination.value.page,
            page_size: pagination.value.page_size
        });
        if (res.status === 1) {
            bookings.value = (res.data.items || []).map((row) => ({
                ...row,
                key: row.id
            }));
            pagination.value.total = res.data.pagination?.total || 0;
        }
    } finally {
        loading.value = false;
    }
};

const load = () => {
    if (displayMode.value === 'calendar') {
        loadCalendar();
        return;
    }
    loadTable();
};

const onFilterChange = () => {
    pagination.value.page = 1;
    loadTable();
};

const openBooking = (record) => {
    if (!record.user_id) return;
    if (record.status === 'confirmed') {
        router.push({
            path: `/consultation-workspace/clients/${record.user_id}`,
            query: { consultation_id: record.id }
        });
        return;
    }
    if (!['no_show', 'cancelled'].includes(record.status)) {
        router.push(`/consultation-workspace/clients/${record.user_id}`);
    }
};

const onCalendarDateChange = (date) => {
    anchorDate.value = date;
    if (calendarView.value === 'month') {
        calendarView.value = 'day';
    }
};

watch(displayMode, (mode) => {
    if (mode === 'calendar') {
        loadCalendar();
    } else {
        loadTable();
    }
});

watch(calendarView, () => scheduleCalendarLoad());
watch(anchorDate, () => scheduleCalendarLoad(), { deep: true });
watch(filter, onFilterChange);

const scheduleCalendarLoad = () => {
    if (displayMode.value !== 'calendar') return;
    if (calendarTimer) clearTimeout(calendarTimer);
    calendarTimer = setTimeout(loadCalendar, 180);
};

const refreshPage = async (force = false) => {
    if (!force && lastLoadedAt.value && Date.now() - lastLoadedAt.value < STALE_MS) {
        return;
    }
    await Promise.all([loadSummary(), load()]);
    lastLoadedAt.value = Date.now();
};

const loadSummary = async () => {
    try {
        const res = await getConsultantDashboard();
        if (res.status === 1) {
            consultant.value = res.data.consultant;
            todayBookings.value = res.data.today_bookings || [];
        }
    } catch {
        /* ignore */
    }
};

onMounted(() => {
    refreshPage(false);
});

onActivated(() => {
    refreshPage(false);
});
</script>

<template>
    <div class="cw-bookings">
        <section class="cw-bookings-panel admin-panel">
            <div class="cw-bookings-intro">
                <div class="cw-bookings-intro__identity">
                    <span class="cw-bookings-intro__name">
                        {{ consultant?.display_name || '諮詢師' }}
                    </span>
                    <ConsultantLevelBadge
                        v-if="consultant?.level"
                        :level="consultant.level"
                    />
                </div>
                <span class="cw-stat cw-stat--accent">
                    今日待諮詢 <strong>{{ todayCount }}</strong> 筆
                </span>
            </div>

            <div
                v-if="todayBookings.length"
                class="cw-bookings-today"
            >
                <div class="cw-bookings-today__label">今日</div>
                <div class="cw-bookings-today__list">
                    <button
                        v-for="item in todayBookings"
                        :key="item.id"
                        type="button"
                        class="cw-bookings-today__item"
                        @click="openBooking(item)"
                    >
                        <span class="cw-bookings-today__time">
                            {{ taipeiDay(item.starts_at).format('HH:mm') }}
                        </span>
                        <span class="cw-bookings-today__name">{{ item.user_name }}</span>
                        <Tag
                            :color="getConsultationBookingStatusMeta(item.status).color"
                            class="cw-bookings-today__tag"
                        >
                            {{ getConsultationBookingStatusMeta(item.status).label }}
                        </Tag>
                    </button>
                </div>
            </div>

            <div class="cw-bookings-toolbar">
                <div class="cw-bookings-toolbar__controls">
                    <div class="cw-bookings-toolbar__group">
                        <span class="cw-bookings-toolbar__label">顯示</span>
                        <Segmented
                            v-model:value="displayMode"
                            class="cw-bookings-segmented"
                            :options="[
                                {
                                    label: '日曆',
                                    value: 'calendar'
                                },
                                {
                                    label: '列表',
                                    value: 'table'
                                }
                            ]"
                        />
                    </div>
                    <div v-if="displayMode === 'calendar'" class="cw-bookings-toolbar__group">
                        <span class="cw-bookings-toolbar__label">範圍</span>
                        <Segmented
                            v-model:value="calendarView"
                            class="cw-bookings-segmented"
                            :options="[
                                { label: '日', value: 'day' },
                                { label: '週', value: 'week' },
                                { label: '月', value: 'month' }
                            ]"
                        />
                    </div>
                    <div v-else class="cw-bookings-toolbar__group">
                        <span class="cw-bookings-toolbar__label">篩選</span>
                        <Segmented
                            v-model:value="filter"
                            class="cw-bookings-segmented"
                            :options="[
                                { label: '今日', value: 'today' },
                                { label: '待諮詢', value: 'upcoming' },
                                { label: '歷史', value: 'history' },
                                { label: '全部', value: 'all' }
                            ]"
                        />
                    </div>
                </div>
                <div class="cw-bookings-toolbar__stats">
                    <span v-if="displayMode === 'calendar'" class="cw-stat">
                        本期 <strong>{{ rangeBookingCount }}</strong> 筆
                    </span>
                    <span v-if="displayMode === 'calendar' && confirmedCount" class="cw-stat cw-stat--accent">
                        待諮詢 <strong>{{ confirmedCount }}</strong>
                    </span>
                    <span v-else-if="displayMode === 'table'" class="cw-stat">
                        共 <strong>{{ pagination.total }}</strong> 筆
                    </span>
                </div>
            </div>

            <Spin :spinning="loading">
                <BookingCalendar
                    v-if="displayMode === 'calendar'"
                    :bookings="bookings"
                    :view-mode="calendarView"
                    :anchor-date="anchorDate"
                    :loading="loading"
                    @update:anchor-date="onCalendarDateChange"
                    @booking-click="openBooking"
                />

                <div v-else class="cw-bookings-table">
                    <Table
                        :columns="columns"
                        :data-source="bookings"
                        :pagination="{
                            current: pagination.page,
                            pageSize: pagination.page_size,
                            total: pagination.total,
                            onChange: (p) => {
                                pagination.page = p;
                                loadTable();
                            }
                        }"
                    >
                        <template #bodyCell="{ column, record }">
                            <template v-if="column.key === 'starts_at'">
                                {{ formatConsultationTimeRange(record.starts_at, record.ends_at) }}
                            </template>
                            <template v-else-if="column.key === 'mode'">
                                <span class="cw-mode">
                                    <VideoCameraOutlined v-if="record.mode !== 'offline'" />
                                    <ShopOutlined v-else />
                                    {{ record.mode === 'offline' ? '實體' : '線上' }}
                                </span>
                            </template>
                            <template v-else-if="column.key === 'status'">
                                <Tag :color="getConsultationBookingStatusMeta(record.status).color">
                                    {{ getConsultationBookingStatusMeta(record.status).label }}
                                </Tag>
                            </template>
                            <template v-else-if="column.key === 'version_name'">
                                <span v-if="record.version_name">{{ record.version_name }}</span>
                                <span v-else class="text-muted">—</span>
                            </template>
                            <template v-else-if="column.key === 'action'">
                                <div class="admin-table-actions">
                                    <Button
                                        v-if="record.user_id && record.status === 'confirmed'"
                                        type="primary"
                                        ghost
                                        size="small"
                                        class="admin-table-actions__btn"
                                        @click="openBooking(record)"
                                    >
                                        進入場次
                                    </Button>
                                    <Button
                                        v-else-if="
                                            record.user_id &&
                                            !['no_show', 'cancelled'].includes(record.status)
                                        "
                                        size="small"
                                        class="admin-table-actions__btn"
                                        @click="openBooking(record)"
                                    >
                                        查看學員
                                    </Button>
                                </div>
                            </template>
                        </template>
                    </Table>
                </div>
            </Spin>
        </section>
    </div>
</template>

<style scoped>
.cw-bookings-panel {
    padding: 20px 22px 24px;
    border-radius: 16px;
    border: 1px solid rgba(196, 163, 90, 0.22);
    background: #fff;
    box-shadow: 0 8px 24px rgba(39, 43, 54, 0.05);
}

.cw-bookings-intro {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
    padding-bottom: 14px;
    border-bottom: 1px solid #f0f2f5;
}

.cw-bookings-intro__identity {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
}

.cw-bookings-intro__name {
    font-size: 17px;
    font-weight: 700;
    color: #272b36;
}

.cw-bookings-today {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 16px;
    padding: 12px 14px;
    border-radius: 12px;
    background: #f8f4eb;
    border: 1px solid #ede4d0;
}

.cw-bookings-today__label {
    flex-shrink: 0;
    padding-top: 6px;
    font-size: 12px;
    font-weight: 700;
    color: #8b6914;
}

.cw-bookings-today__list {
    flex: 1;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    min-width: 0;
}

.cw-bookings-today__item {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    border: 1px solid #e8edf2;
    border-radius: 999px;
    background: #fff;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.cw-bookings-today__item:hover {
    border-color: #d6d0c4;
    box-shadow: 0 2px 8px rgba(43, 43, 43, 0.08);
}

.cw-bookings-today__time {
    font-size: 12px;
    font-weight: 700;
    color: #8b6914;
    font-variant-numeric: tabular-nums;
}

.cw-bookings-today__name {
    font-size: 13px;
    font-weight: 600;
    color: #0f172a;
}

.cw-bookings-today__tag {
    margin: 0;
    font-size: 10px;
    line-height: 16px;
}

.cw-bookings-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 18px;
    padding-bottom: 16px;
    border-bottom: 1px solid #f0f2f5;
}

.cw-bookings-toolbar__controls {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 16px 20px;
}

.cw-bookings-toolbar__group {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.cw-bookings-toolbar__label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: #94a3b8;
    text-transform: uppercase;
}

.cw-bookings-toolbar__stats {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.cw-stat {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    border-radius: 999px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    font-size: 13px;
    color: #64748b;
}

.cw-stat strong {
    color: #0f172a;
    font-weight: 700;
}

.cw-stat--accent {
    border-color: rgba(139, 105, 20, 0.25);
    background: #f6f1e6;
    color: #8b6914;
}

.cw-stat--accent strong {
    color: #8b6914;
}

.cw-bookings-segmented :deep(.ant-segmented) {
    background: #f1f5f9;
    padding: 3px;
    border-radius: 10px;
}

.cw-bookings-segmented :deep(.ant-segmented-item-selected) {
    border-radius: 8px !important;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
}

.cw-bookings-table {
    margin-top: 4px;
}

.cw-mode {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #475569;
    font-size: 13px;
}

.text-muted {
    color: #94a3b8;
    font-size: 12px;
}
</style>
